import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, Layout, Menu } from 'antd';
import { ThemeProvider } from 'dlweb-components';
import styled from 'styled-components';
import SidebarButton from './SidebarButton';

const { Sider } = Layout;

const Sidebar = ({$location, $rootScope, allowedNodes, enableMessages, enablePortal, webViewMode, readOnly}) => {

  const [collapsed, setCollapsed] = useState(true);
  const [currentSelection, setCurrentSelection] = useState('');
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {

    const setActiveButton = () => {

      const path = $location.path().substring(1, $location.path().length);
      const search = $location.search();

      switch (path.toLowerCase()) {
        case 'workflow':
          const view = search.view && search.view.toLowerCase();
          if (!view || view === 'tasks') {
            setCurrentSelection('workflow');
          }
          else if (view === 'history') {
            setCurrentSelection('workflow-history');
          }
          else if (view === 'messages') {
            setCurrentSelection('messages');
          }
          break;
        case 'documents':
          const folder = search.folder && search.folder.toLowerCase();
          if (folder === 'favorites') {
            setCurrentSelection('favorites');
          }
          else if (folder === 'recyclebin') {
            setCurrentSelection('recyclebin');
          } else {
            setCurrentSelection(path);
          }
          break;
        default:
          setCurrentSelection(path);
          break;
      }
    }

    // set active button on load
    setActiveButton();

    // listen for route change
    const cleanup = $rootScope.$on('$locationChangeStart', function (event, next, current) {
      setActiveButton();
    });

    return () => {
      cleanup();
    };
  }, [$location, $rootScope])

  const onCollapse = collapsed => {
    setCollapsed(collapsed);
  };

  const onSelect = e => {
    setTimeout(() => {
      switch (e.key) {
        case 'portal':
          $location.url('/portal');
          $rootScope.$apply()
          break;
        case 'documents':
          setCurrentSelection(e.key)
          $location.url('/documents');
          $rootScope.$apply()
          break;
        case 'reports':
          $location.url('/reports');
          $rootScope.$apply()
          break;
        case 'projects':
          $location.url('/projects');
          $rootScope.$apply()
          break;
        case 'search':
          $location.url('/search');
          $rootScope.$apply()
          break;
        case 'saved-search':
          $location.url('/saved-search');
          $rootScope.$apply()
          break;
        case 'last30Days':
          $location.url('/saved-search').search('search=Last%2030%20Days&page=1');
          $rootScope.$apply()
          break;
        case 'last30Docs':
          $location.url('/saved-search').search('search=Last%2030%20Docs&page=1');
          $rootScope.$apply()
          break;
        case 'myCheckedOutDocs':
          $location.url('/saved-search').search('search=My%20Checked%20Out%20Docs&page=1');
          $rootScope.$apply()
          break;
        case 'myWorkflowTasks':
          $location.url('/saved-search').search('search=My%20Workflow%20Tasks&page=1');
          $rootScope.$apply()
          break;
        case 'messages':
          setCurrentSelection(e.key)
          $location.url('/workflow').search('view=messages');
          $rootScope.$apply()
          break;
        case 'workflow':
          $location.url('/workflow').search('view=tasks');
          $rootScope.$apply()
          break;
        case 'workflow-history':
          $location.url('/workflow').search('view=history');
          $rootScope.$apply()
          break;
        case 'favorites':
          $location.url('/documents').search('folder=favorites');
          $rootScope.$apply()
          break;
        case 'recyclebin':
          $location.url('/documents').search('folder=recyclebin');
          $rootScope.$apply()
          break;
        case 'admin':
          $location.url('/admin');
          $rootScope.$apply()
          break;
        case 'configuration/users':
          $location.url('/configuration/users');
          $rootScope.$apply()
          break;
        case 'configuration/groups':
          $location.url('/configuration/groups');
          $rootScope.$apply()
          break;
        case 'help':
          window.open('help', '_blank');
          break;
        default:
          break;
      }
    }, 100)
  }

  useEffect(() => {
    
    const items = [];

    enablePortal && items.push({
      key: 'portal',
      icon: <SidebarButton iconType="home" />,
      label: 'Portal'
    })

    allowedNodes.includes('documents') && items.push({
      key: 'documents',
      icon: <SidebarButton iconType="file" />,
      label: 'Documents'
    })
  
    enableMessages && items.push({
      key: 'messages',
      icon: <SidebarButton iconType="envelope" />,
      label: 'Messages'
    })
    
    allowedNodes.includes("workflow") && items.push({
      key: "subWorkflow",
      icon: <SidebarButton iconType="cogs" />,
      label: 'Workflow',
      children: [
        {
          key: "workflow",
          icon:  <SidebarButton iconType="cogs" />,
          label: 'Workflow Tasks'
        },
        {
          key: "workflow-history",
          icon: <SidebarButton iconType="history" />,
          label: 'Workflow History'
        }
      ],
    });

    allowedNodes.includes('reports') && items.push({
      key: 'reports',
      icon: <SidebarButton iconType="chart-bar" />,
      label: 'Reports'
    })

    !webViewMode &&
    !readOnly &&
    allowedNodes.includes('projects') &&
    items.push({
      key: 'projects',
      icon: <SidebarButton iconType="project-diagram" />,
      label: 'Projects'
    })

    allowedNodes.includes('search') && items.push({
      key: 'subSearch',
      icon: <SidebarButton iconType="search" />,
      label: 'Searches',
      children: [
        {
          key: 'search',
          icon: <SidebarButton iconType="search" />,
          label: 'Advanced Search'
        },
        {
          key: 'saved-search',
          icon: <SidebarButton iconType="search-plus" text="" />,
          label: 'Saved Searches'
        },
        {
          type: 'divider'
        },
        {
          key: 'last30Days',
          icon: <SidebarButton iconType="history" />,
          label: 'Last 30 Days'
        },
        {
          key: 'last30Docs',
          icon: <SidebarButton iconType="history" />,
          label: 'Last 30 Docs'
        },
        {
          key: 'myCheckedOutDocs',
          icon: <SidebarButton iconType="check" />,
          label: 'My Checked Out Docs'
        },
        {
          key: 'myWorkflowTasks',
          icon: <SidebarButton iconType="cogs" />,
          label: 'My Workflow Tasks'
        }
      ]
    })

    items.push({
      key: 'favorites',
      icon: <SidebarButton iconType="star" />,
      label: 'Favorites'
    })

    !webViewMode &&
    !readOnly &&
    allowedNodes.includes('recycle bin') &&
    items.push({
      key: 'recyclebin',
      icon: <SidebarButton iconType="trash" text="Recycle Bin" />,
      label: 'Recycle Bin'
    })

    if(!webViewMode && !readOnly && allowedNodes.includes('repository configuration')) {
      var configMenu = {
        key: 'subConfiguration',
        icon: <SidebarButton iconType="sliders-h" text="Repository Configuration" />,
        label: 'Repository Configuration',
        children: []
      }
      
      allowedNodes.includes('group manager') &&
      configMenu.children.push(
        {
          key: 'configuration/groups',
          icon:<SidebarButton iconType="users-cog" />,
          label: 'Group Manager'
        }
      )

      allowedNodes.includes('user manager') &&
      configMenu.children.push(
        {
          key: 'configuration/users',
          icon:<SidebarButton iconType="user-cog" />,
          label: 'User Manager'
        }
      )

      items.push(configMenu);
    }

    setMenuItems(items);

  }, [allowedNodes, enableMessages, enablePortal, readOnly, webViewMode])

  return (
    <ThemeProvider componentToken={{Menu: {collapsedWidth: 50, itemHeight: 50, itemBg: '#f9f9fa', activeBarBorderWidth: 0}}}>
      <SidebarContainer>
      <Layout
          style={{
            minHeight: '100vh',
          }}
        >
          <Sider
            width="300"
            collapsible 
            collapsedWidth={50}
            collapsed={collapsed}
            onCollapse={onCollapse}
          >
            <MainMenu>
              <Menu
                mode="inline"
                items={menuItems}
                selectedKeys={[currentSelection]}
                defaultOpenKeys={!collapsed ? ['sub1'] : []}
                onClick={onSelect}
              />
            </MainMenu>
            <HelpMenu>
              <Menu
                mode="inline"
                items={[
                  {
                    key: 'help',
                    icon: <SidebarButton iconType="question-circle" text="Help" />,
                    label: 'Help'
                  }
                ]}
                onClick={onSelect}
                selectedKeys={[currentSelection]}
              />
            </HelpMenu>
          </Sider>
        </Layout>
      </SidebarContainer>
    </ThemeProvider>
  );
}

const SidebarContainer = styled.div`
  height: 100%;
  overflow: hidden;
  background-color: #f9f9fa;
  border-right: 1px solid #ddd;
`

const MainMenu = styled.div`
  height: calc(100% - 160px);
  overflow: hidden auto;
`

const HelpMenu = styled.div`
  width: 100%;
  position: absolute;
  bottom: 105px;
  left: 0;
  .ant-menu-item {
    padding-inline: 13px;
  }
`

const mapStateToProps = state => {
  const { isAdmin, readOnly, allowedNodes } = state.user;
  const { webViewMode, enablePortal, enableMessages } = state.app;
  return { isAdmin, readOnly, webViewMode, enablePortal, enableMessages, allowedNodes };
};

export default connect(mapStateToProps)(Sidebar);